import { useEffect, useState } from 'react';
import styled from 'styled-components';

import MaterialFinder from '/components/layout/backend/MaterialFinder';
import { colors, effects } from '/css';
import isTrue from '/lib/utils/isTrue';
import SlideShow from '/components/ui/SlideShow';
import MaterialListItem from './MaterialListItem';

const MaterialList = ({
    materials = [],
    onUpdate = (updatedMaterials) => {},
    withTitles = false,
    readOnly = false,
    className = '',
    hidePrices = false,
    hideQuantity = false,
    hideActions = false,
    hideSearch = false,
    withIncludedOnReceiptCheckbox = false,
    withIncludedProductCheckbox = false,
    onIncludedOnReceiptToggle = (articleNo) => {},
    materialsIncludedOnReceipt = [],
}) => {
    const [lightboxImages, setLightboxImages] = useState([]);

    useEffect(() => {
        if (hidePrices) return;

        const materialsHavePrices = materials.reduce(
            (acc, m) => (!m.price?.price ? false : acc),
            true
        );

        const spotonPricesMissing = materials.reduce(
            (acc, m) => (!m.price?.spotonPrice ? true : acc),
            false
        );
        if (materialsHavePrices && spotonPricesMissing) {
            onUpdate(
                materials.map((m) => ({
                    ...m,
                    price: {
                        ...m.price,
                        spotonPrice: m.price?.spotonPrice || m.price?.price,
                    },
                }))
            );
        }
    }, []);

    const addItemToList = (item) => {
        if (materials.some((m) => m.articleNo === item.articleNo)) {
            alert('Material already added');
            return;
        }
        onUpdate([
            {
                ...item,
                price: {
                    ...item.price,
                    spotonPrice: item.price?.spotonPrice || item.price?.price,
                },
            },
            ...materials,
        ]);
    };

    const removeItemFromList = (item) => {
        onUpdate(
            materials.filter(
                (material) => material.articleNo !== item.articleNo
            )
        );
    };

    const updateItemQuantity = (quantity, item) => {
        onUpdate(
            materials.map((material) => {
                return material.articleNo === item.articleNo
                    ? { ...material, quantity: Number(quantity) }
                    : material;
            })
        );
    };

    const toggleIsProductFlag = (item) => {
        onUpdate(
            materials.map((material) => {
                return material.articleNo === item.articleNo
                    ? { ...material, isProduct: !isTrue(material.isProduct) }
                    : material;
            })
        );
    };

    const updateItemPrice = (price, item) => {
        const newPrice = price
            .replace(/[^\d.-]/g, '')
            .replace(/(?!^)-/g, '')
            .replace('.', '%FD%')
            .replace(/\./g, '')
            .replace('%FD%', '.');
        onUpdate(
            materials.map((material) => {
                return material.articleNo === item.articleNo
                    ? {
                          ...material,
                          price: { ...item.price, spotonPrice: newPrice },
                      }
                    : material;
            })
        );
    };

    const total = hidePrices
        ? 0
        : parseFloat(
              (materials || []).reduce((acc, mat) => {
                  return (acc +=
                      Number(mat.price?.spotonPrice || mat.price?.price) *
                      Number(mat.quantity));
              }, 0)
          ).toFixed(2);

    const onImageClick = (material) => {
        setLightboxImages(
            (material.picture || []).map((img) => {
                if (img.uRL)
                    return {
                        ...img,
                        url: img.uRL,
                    };
                return img;
            })
        );
    };

    return (
        <MaterialListGeneratorWrapper className={className}>
            {!readOnly && !hideSearch && (
                <MaterialFinder
                    onItemSelected={addItemToList}
                    hidePrices={hidePrices}
                    className="mt-24 mb-32"
                />
            )}

            {(materials || []).length > 0 && (
                <ul id="material-list">
                    <li
                        className={[
                            'header',
                            withTitles ? 'with-title' : null,
                        ].join(' ')}
                    >
                        <span className="img">Bilde</span>
                        <span className="code">Artikkelnr.</span>
                        {withTitles && (
                            <span className="name">Artikkenavn</span>
                        )}
                        {!hidePrices && <span className="price">Pris</span>}
                        {!readOnly && (
                            <span className="price input">
                                <div />
                            </span>
                        )}
                        {!hideQuantity && <span className="qty">Kvantum</span>}
                        {withIncludedOnReceiptCheckbox && (
                            <span
                                className="receipt"
                                title="Om varen skal vises på kundens kvittering"
                            >
                                Kvittering
                            </span>
                        )}
                        {withIncludedProductCheckbox && (
                            <span className="product">Produkt</span>
                        )}
                        {!readOnly && !hideActions && (
                            <span className="actions">
                                <div />
                            </span>
                        )}
                    </li>
                    {materials.map((item, i) => {
                        return (
                            <MaterialListItem
                                key={i}
                                material={item}
                                className={[
                                    withTitles ? 'with-title' : null,
                                    'material-list-item',
                                    'material-list-item-' + i,
                                ].join(' ')}
                                onImageClick={onImageClick}
                                hidePrice={hidePrices}
                                hideQuantity={hideQuantity}
                                readOnly={readOnly}
                                withIncludedProductCheckbox={
                                    withIncludedProductCheckbox
                                }
                                onChangePrice={(price) =>
                                    updateItemPrice(price, item)
                                }
                                onChangeQuantity={(quantity) =>
                                    updateItemQuantity(quantity, item)
                                }
                                onToggleIsProduct={() =>
                                    toggleIsProductFlag(item)
                                }
                                onRemove={() => removeItemFromList(item)}
                                hideActions={hideActions}
                                withIncludedOnReceiptCheckbox={
                                    withIncludedOnReceiptCheckbox
                                }
                                onIncludedOnReceiptToggle={
                                    onIncludedOnReceiptToggle
                                }
                                isIncludedOnReceipt={materialsIncludedOnReceipt.includes(
                                    item.articleNo
                                )}
                                withTitle={withTitles}
                            />
                        );
                    })}
                    {!hidePrices && (
                        <li className="footer">
                            <p>
                                Total: <span>Kr {total || '--'}</span>
                            </p>
                        </li>
                    )}
                </ul>
            )}

            {!!lightboxImages.length && (
                <SlideShow
                    images={lightboxImages}
                    onClose={() => setLightboxImages([])}
                />
            )}
        </MaterialListGeneratorWrapper>
    );
};

export default MaterialList;

const MaterialListGeneratorWrapper = styled.div`
    width: 100%;
    > ul {
        background: #fff;
        li {
            border-bottom: 3px solid ${colors.grey3};
            display: flex;
            padding: 18px 8px;
            position: relative;
            @media (max-width: 767px) {
                flex-wrap: wrap;
            }
            span {
                display: flex;
                align-items: center;
                position: relative;
                input:not([type='checkbox']) {
                    border-radius: 12px;
                    font-size: 16px;
                    padding: 0 24px 2px 24px;
                    display: flex;
                    align-items: center;
                    height: 46px;
                    ${effects.shadow};
                    border: none;
                }
                &.img {
                    min-width: 60px;
                    max-width: 60px;
                    img {
                        display: block;
                        cursor: pointer;
                    }
                }
                &.code {
                    min-width: auto;
                    max-width: auto;
                    flex: 1 1 auto;
                    padding: 0 18px;
                    font-family: 'gellix-semi';
                }
                &.name {
                    flex: 1 1 auto;
                    padding: 0 18px;
                    font-family: 'gellix-semi';
                }
                &.receipt {
                    width: 90px;
                    display: inline-block;
                    align-self: center;
                    text-align: right;
                    padding-left: 20px;
                }
                &.product {
                    width: 80px;
                    display: inline-block;
                    align-self: center;
                    text-align: right;
                    padding-left: 20px;
                }
                &.price {
                    width: 80px;
                    min-width: 80px;
                    max-width: 80px;
                    &.input {
                        width: 120px;
                        min-width: 120px;
                        max-width: 120px;
                        small {
                            font-size: 16px;
                            color: ${colors.grey6};
                            position: absolute;
                            top: 50%;
                            left: 12px;
                            transform: translateY(-50%);
                            padding-bottom: 2px;
                        }
                        input {
                            padding-left: 32px;
                        }
                    }
                }
                &.qty {
                    width: 85px;
                    min-width: 85px;
                    max-width: 85px;
                    padding-left: 18px;
                }
                &.actions {
                    width: 30px;
                    min-width: 30px;
                    max-width: 30px;
                    display: flex;
                    justify-content: flex-end;
                }
            }
            &.with-title {
                .code {
                    min-width: 100px;
                    max-width: 100px;
                    flex: none;
                }
            }
            &.header {
                span {
                    font-family: 'gellix-medium';
                    color: ${colors.grey6};
                }
            }
            &.footer {
                justify-content: flex-end;
                border-bottom: none;
                font-family: 'gellix-medium';
                color: ${colors.grey6};
                span {
                    color: ${colors.black};
                    display: inline-block;
                    margin-left: 8px;
                }
            }
            @media (max-width: 767px) {
                &.header {
                    display: none;
                }
                > span {
                    width: 100% !important;
                    max-width: 100% !important;
                    padding: 0 !important;
                    margin-bottom: 8px;
                    flex-wrap: wrap;
                    &:before {
                        content: '-';
                        display: block;
                        width: 100%;
                        margin-bottom: 4px;
                        color: ${colors.grey6};
                    }
                    &.img {
                        max-width: 50px !important;
                        &:before {
                            display: none;
                        }
                    }
                    &.code:before {
                        content: 'Arikkelnr.';
                    }
                    &.name:before {
                        content: 'Arikkelnavn';
                    }
                    &.qty:before {
                        content: 'Kvantum';
                    }
                    &.price:before {
                        content: 'Pris';
                    }
                    &.price.input small {
                        top: 48px;
                    }
                    &.price.input:before {
                        content: 'SpotOn pris';
                    }
                    &.receipt:before {
                        content: 'Kvittering';
                    }
                    &.receipt {
                        text-align: left;
                    }
                    &.product {
                        text-align: left;
                    }
                    &.product:before {
                        content: 'Produkt';
                    }
                    &.actions {
                        margin: 0;
                        position: absolute;
                        top: 18px;
                        right: 8px;
                        &:before {
                            display: none;
                        }
                    }
                }
            }
        }
    }
`;
